import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Container,
  Backdrop,
  CircularProgress,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  Slide
} from "@mui/material";
import bkstApi from "./api";
import SendIcon from "@mui/icons-material/Send";
import { capitalize, equalsIgnoreCase, getQueryParam } from "./util";
import AiQuote from "../../ai/component/AiQuote";
import { TypeAnimation } from "react-type-animation";

export default function Gemini(props) {
  const src = getQueryParam(window.location.search, "src");

  const ref = useRef(null);

  const [templates, setTemplates] = useState("");

  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [complexityResponse, setComplexityResponse] = useState("");
  const [summary, setSummary] = useState("");
  const [showPricing, setShowPricing] = useState(false);

  const [servings, setServings] = useState("");
  const [productSubtype, setProductSubtype] = useState("");

  const [edit, setEdit] = useState(false);
  const [revisedImage, setRevisedImage] = useState("");

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    bkstApi(`/admin/gemini/prompt`).then((res) => {
      setTemplates(res.data);
    });
  }, []);

  const generateSummary = (response) => {
    console.log("->", response);

    if (response["2d_elements"] || response["3d_elements"]) {
      const summary = {
        figurineBasedLevel: 0,
        complexityBasedLevel: 0,
        TWO_D: {
          count: 0,
          surfaceArea: 0,
          cost: 0,
          effort: 0,
          lowComplexity: 0,
          mediumComplexity: 0,
          highComplexity: 0
        },
        THREE_D: {
          count: 0,
          surfaceArea: 0,
          cost: 0,
          effort: 0,
          lowComplexity: 0,
          mediumComplexity: 0,
          highComplexity: 0
        }
      };
      for (const decoration of response["2d_elements"]) {
        summary.TWO_D.count = +summary.TWO_D.count + decoration.count;
        summary.TWO_D.surfaceArea = +summary.TWO_D.surfaceArea + +decoration["surface_area"] * decoration.count;
        summary.TWO_D.cost = +summary.TWO_D.cost + decoration.cost;
        summary.TWO_D.effort = +summary.TWO_D.effort + decoration.effort;

        if (decoration.complexity === "low") {
          summary.TWO_D.lowComplexity = +summary.TWO_D.lowComplexity + decoration.count;
        } else if (decoration.complexity === "medium") {
          summary.TWO_D.mediumComplexity = +summary.TWO_D.mediumComplexity + decoration.count;
        } else if (decoration.complexity === "high") {
          summary.TWO_D.highComplexity = +summary.TWO_D.highComplexity + decoration.count;
        }
      }
      for (const decoration of response["3d_elements"]) {
        summary.THREE_D.count = +summary.THREE_D.count + decoration.count;
        summary.THREE_D.surfaceArea = +summary.THREE_D.surfaceArea + +decoration["surface_area"] * decoration.count;
        summary.THREE_D.cost = +summary.THREE_D.cost + decoration.cost;
        summary.THREE_D.effort = +summary.THREE_D.effort + decoration.effort;

        if (decoration.complexity === "low") {
          summary.THREE_D.lowComplexity = +summary.THREE_D.lowComplexity + decoration.count;
        } else if (decoration.complexity === "medium") {
          summary.THREE_D.mediumComplexity = +summary.THREE_D.mediumComplexity + decoration.count;
        } else if (decoration.complexity === "high") {
          summary.THREE_D.highComplexity = +summary.THREE_D.highComplexity + decoration.count;
        }
      }

      // handmade figurines?
      const handmadeFigurines = response["3d_elements"].filter((o) => o.type.includes("figurine") && o.manufactured === "handmade");
      const totalHandmadeFigurines = handmadeFigurines.reduce((acc, o) => +acc + +o.count, 0);

      if (totalHandmadeFigurines >= 3) {
        summary.figurineBasedLevel = 5;
      } else if (totalHandmadeFigurines > 1) {
        summary.figurineBasedLevel = 4;
      } else if (totalHandmadeFigurines === 1) {
        summary.figurineBasedLevel = 3;
      } else {
        // check 2D decorations
        // if any 2d decoration other than piping, then level 2
        const nonPipingDecoration = response["2d_elements"].filter((o) => !o.type.includes("piping"));
        if (nonPipingDecoration.length > 0) {
          summary.figurineBasedLevel = 2;
        } else {
          summary.figurineBasedLevel = 1;
        }
      }

      // Check complexity of decoration
      if (summary.THREE_D.highComplexity >= 3) {
        summary.complexityBasedLevel = 5;
      } else if (summary.THREE_D.highComplexity > 1) {
        summary.complexityBasedLevel = 4;
      } else if (summary.THREE_D.highComplexity === 1 || summary.TWO_D.highComplexity > 2) {
        summary.complexityBasedLevel = 3;
      } else {
        if (summary.TWO_D.highComplexity > 0 || summary.TWO_D.mediumComplexity > 0) {
          summary.complexityBasedLevel = 2;
        } else {
          summary.complexityBasedLevel = 1;
        }
      }

      setSummary(summary);
    }
  };

  const analyzeTheme = () => {
    setLoading(true);
    setProcessing(true);
    setShowPricing(false);

    setTimeout(() => {
      ref.current?.scrollIntoView(true);
    }, 1000);

    bkstApi.post(`/gemini`, { action: "scan", img: src, prompt: templates["theme"] }).then((res) => {
      setResponse({ ...res.data.json, tags: res.data.tags });
      setServings(res.data.json.servings);

      const productSubtype = getProductSubtype(res.data.json);
      setProductSubtype(productSubtype);

      setLoading(false);
      setProcessing(false);
    });
  };

  const editImage = () => {
    setLoading(true);
    bkstApi.post(`/gemini`, { action: "edit", img: src, prompt: "change the background to black" }).then((res) => {
      console.log(res.data);
      setRevisedImage(res.data.img);
      setLoading(false);
    });
  };

  const analyzeComplexity = () => {
    bkstApi.post(`/gemini`, { img: src, prompt: templates["complexity"] }).then((res) => {
      setComplexityResponse(res.data.json);
      generateSummary(res.data.json);
      setLoading(false);
    });
  };

  const getProductSubtype = (response) => {
    if (equalsIgnoreCase(response.type, "cake")) {
      if (equalsIgnoreCase(response.shape, "round") || equalsIgnoreCase(response.shape, "square")) {
        return `${response.tier}-Tier`;
      } else if (equalsIgnoreCase(response.shape, "rectangle")) {
        return "Sheet";
      } else if (response.shape.toLowerCase().includes("heart")) {
        return "Heart";
      } else {
        return response.shape;
      }
    } else {
      return response.shape;
    }
  };

  return (
    <Container maxWidth={"md"}>
      {loading && <LinearProgress />}
      <Box>
        <Box my={5}>
          <Grid container spacing={4}>
            <Grid item sm={6} xs={12}>
              <Box sx={{ display: "unset" }}>
                <img src={src} style={{ width: "100%" }} />
              </Box>
              <Button fullWidth variant="contained" onClick={analyzeTheme}>
                Analyze
              </Button>
              {/*<Button fullWidth variant="contained" color="secondary" onClick={editImage}>
                Edit
              </Button> */}
              {revisedImage && <img src={revisedImage} style={{ width: "100%" }} />}
            </Grid>
            <Grid item sm={6} xs={12}>
              <Paper elevation={0}>
                {/* <Box p={1}>
                  {Object.entries(templates).map(([k, v]) => (
                    <Chip
                      key={k}
                      size="small"
                      color="primary"
                      label={k}
                      component="a"
                      onClick={() => {
                        edit ? setPrompt(v) : sendToGemini(v);
                      }}
                      clickable
                      sx={{ marginRight: "3px" }}
                    />
                  ))}
                  <Chip
                    size="small"
                    color="secondary"
                    label="edit prompt"
                    component="a"
                    onClick={() => setEdit(true)}
                    clickable
                    sx={{ marginRight: "3px" }}
                  />
                </Box> */}
              </Paper>
              {/* edit && (
                <Box py={2}>
                  <TextField
                    id="prompt"
                    value={prompt}
                    name="prompt"
                    variant="outlined"
                    multiline
                    rows={10}
                    fullWidth
                    required
                    autoFocus
                    placeholder="Type something"
                    onChange={(e) => setPrompt(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            color="primary"
                            variant="contained"
                            disabled={prompt.length === 0}
                            onClick={() => sendToGemini(prompt)}
                          >
                            <SendIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        paddingRight: 0
                      },
                      backgroundColor: "#FFFFFF"
                    }}
                  />
                </Box>
              )*/}

              {/* <Box mb={2} sx={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                <Button variant="outlined" onClick={() => setServings("10")}>
                  10
                </Button>
                <Button variant="outlined" onClick={() => setServings("20")}>
                  20
                </Button>
                <Button variant="outlined" onClick={() => setServings("30")}>
                  30
                </Button>
                <Button variant="outlined" onClick={() => setServings("40")}>
                  40
                </Button>
                <Button variant="outlined" onClick={() => setServings("50")}>
                  50
                </Button>
                <Button variant="outlined" onClick={() => setServings("60")}>
                  60
                </Button>
                <Button variant="outlined" onClick={() => setServings("70")}>
                  70
                </Button>
              </Box> */}
              <Box ref={ref} mb={100}>
              {response && (
                <Box>
                  <Box mb={3}>
                    <Grid container spacing={4}>
                      <Grid item xs={6}>
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel id="productType-label">Product Subtype</InputLabel>
                          <Select
                            value={productSubtype}
                            label="Product Subtype"
                            id="productSubtype"
                            name="productSubtype"
                            onChange={(e) => setProductSubtype(e.target.value)}
                          >
                            {["1-Tier", "2-Tier", "3-Tier", "Heart", "Sculpted", "Sheet"].map((i) => (
                              <MenuItem key={i} value={i}>
                                {i}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="servings"
                          value={servings}
                          variant="outlined"
                          required
                          fullWidth
                          id="servings"
                          label="Servings"
                          onChange={(e) => setServings(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box p={2} my={2} sx={{ backgroundColor: "#ECECEC" }}>
                    <TypeAnimation
                      splitter={(str) => str.split(/(?= )/)} // 'Lorem ipsum dolor' -> ['Lorem', ' ipsum', ' dolor']
                      sequence={[
                        response.summary,
                        3000,
                        () => {
                          console.log("done typing!");
                          setShowPricing(true);
                        }
                      ]}
                    />
                  </Box>
                  {/* <Box my={2}>
                    <Typography variant="overline" color="textSecondary">
                      {capitalize(response.type)} | {productSubtype} | COMPLEXITY: {response.level} | SERVINGS: {servings}
                    </Typography>
                  </Box> */}
                  {showPricing && (
                    <Box my={2}>
                      <Typography variant="overline" color="textSecondary">
                        AI-Quote Options
                      </Typography>
                      <Slide direction="down" in={true} mountOnEnter unmountOnExit timeout={500}>
                        <Box>
                          <AiQuote
                            bakerId="m7w2nldh2VlpJijvH2tC"
                            imageSrc={src}
                            productType={capitalize(response.type)}
                            productSubtype={productSubtype}
                            complexity={response.level}
                            servings={servings}
                          />
                        </Box>
                      </Slide>
                    </Box>
                  )}
                </Box>
              )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* <Box mt={40}>
        {summary && summary.complexityBasedLevel && (
          <Box sx={{ display: "flex", gap: "20px" }}>
            <Box p={2} sx={{ backgroundColor: "#ECECEC" }}>
              <Typography variant="h5">{summary.complexityBasedLevel}</Typography>
              <Typography variant="overline">AI-based </Typography>
            </Box>
            <Box p={2} sx={{ backgroundColor: "#ECECEC" }}>
              <Typography variant="h5">{summary.figurineBasedLevel}</Typography>
              <Typography variant="overline">Figurine-based </Typography>
            </Box>
          </Box>
        )}
        {response && (
          <Box py={2}>
            <pre style={{ fontSize: "10px", color: "#FFF", backgroundColor: "#000", textAlign: "left" }}>{JSON.stringify(response, null, 2)}</pre>
          </Box>
        )}
        {complexityResponse && (
          <Box py={2}>
            <pre style={{ fontSize: "10px", color: "#FFF", backgroundColor: "#000", textAlign: "left" }}>
              {JSON.stringify(complexityResponse, null, 2)}
            </pre>
          </Box>
        )}
        {summary && (
          <Box py={2}>
            <pre style={{ fontSize: "10px", color: "#FFF", backgroundColor: "#000", textAlign: "left" }}>{JSON.stringify(summary, null, 2)}</pre>
          </Box>
        )}
      </Box> */}
      {processing && (
        <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Container>
  );
}
